import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Input, Label, FormGroup } from 'reactstrap';
import './FilterPanel.css';

const FilterPanel = ({ isOpen, toggle, filters, onFilterChange, options }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Use options from backend
  const uniqueValues = {
    degrees: options?.degrees || [],
    programs: options?.programs || [],
    genders: options?.genders || [],
    states: options?.states || [],
    countries: options?.countries || [],
    statuses: options?.statuses || [],
    reviewers: options?.reviewers || [],
    themes: options?.themes || [],
    reviewStatuses: options?.reviewStatuses || [],
    institutions: options?.institutions || [],
    fieldOfStudy: options?.fieldOfStudy || [],
    graduationYear: options?.graduationYear || [],
    organizations: options?.organizations || [],
    designations: options?.designations || [],

  };

  const handleApply = () => {
    onFilterChange(localFilters);
    toggle();
  };

  const handleReset = () => {
    const resetFilters = {
      dateRange: { start: '', end: '' },
      round1Score: { min: '', max: '' },
      round2Score: { min: '', max: '' },
      average: { min: '', max: '' },
      degrees: [],
      program: [],
      themes: [],
      gender: [],
      state: [],
      country: [],
      status: [],
      reviewer: [],
      unassigned: false,
      institutions: [],
      fieldOfStudy: [],
      graduationYear: { from: '', to: '' },
      organizations: [],
      designations: [],
    };
    setLocalFilters(resetFilters);
  };

  const toggleMultiSelect = (field, value) => {
    setLocalFilters((prev) => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  // Add this state at the top of your FilterPanel component
  const [expandedSections, setExpandedSections] = useState({
    education: false,
    professional: false
  });

  // Add this function to toggle sections
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const MultiSelectDropdown = ({ label, field, options }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selected = localFilters[field] || [];

    return (
      <FormGroup>
        <Label>{label}</Label>
        <div className="position-relative">
          <Button
            color="light"
            className="w-100 d-flex justify-content-between align-items-center"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="text-muted">
              {selected.length > 0 ? `${selected.length} selected` : `Select ${label}`}
            </span>
            <span>▼</span>
          </Button>
          {isOpen && (
            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow" style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}>
              {options.map((option) => (
                <div
                  key={option}
                  className="d-flex align-items-center gap-2 px-3 py-2 hover-bg-light cursor-pointer"
                  onClick={() => toggleMultiSelect(field, option)}
                >
                  <Input
                    type="checkbox"
                    checked={selected.includes(option)}
                    readOnly
                  />
                  <span className="small">{option}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </FormGroup>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="filter-modal">
      <ModalHeader toggle={toggle}>
        <span>☑️</span> Filter Applications
      </ModalHeader>
      <ModalBody>
        <div className="">
          {/* Date Range */}
          <Row >
            <Col md={6}>
              <FormGroup>
                <Label>Date Range</Label>
                <Input
                  type="date"
                  value={localFilters.dateRange.start}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      dateRange: { ...localFilters.dateRange, start: e.target.value },
                    })
                  }
                  placeholder="dd-mm-yyyy"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="invisible">End</Label>
                <Input
                  type="date"
                  value={localFilters.dateRange.end}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      dateRange: { ...localFilters.dateRange, end: e.target.value },
                    })
                  }
                  placeholder="dd-mm-yyyy"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Round 1 Score */}
          <Row >
            <Col md={6}>
              <FormGroup>
                <Label>Round 1 Score</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.round1Score.min}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      round1Score: { ...localFilters.round1Score, min: e.target.value },
                    })
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>to</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={localFilters.round1Score.max}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      round1Score: { ...localFilters.round1Score, max: e.target.value },
                    })
                  }
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Round 2 Score */}
          <Row >
            <Col md={6}>
              <FormGroup>
                <Label>Round 2 Score</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.round2Score.min}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      round2Score: { ...localFilters.round2Score, min: e.target.value },
                    })
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>to</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={localFilters.round2Score.max}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      round2Score: { ...localFilters.round2Score, max: e.target.value },
                    })
                  }
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Average Score */}
          <Row >
            <Col md={6}>
              <FormGroup>
                <Label>Average Score</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.average.min}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      average: { ...localFilters.average, min: e.target.value },
                    })
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>to</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={localFilters.average.max}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      average: { ...localFilters.average, max: e.target.value },
                    })
                  }
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Degree and Program */}
          <Row>
            <Col md={6}>
              <MultiSelectDropdown
                label="Program Applied For"
                field="program"
                options={uniqueValues.programs}
              />
            </Col>
            <Col md={6}>
              <FormGroup>
                <MultiSelectDropdown
                  label="Themes (Select up to 3)"
                  field="themes"
                  options={uniqueValues.themes}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="mb-4">
                <h6
                  className="mb-3 d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSection('education')}
                >
                  Education {expandedSections.education ? '▼' : '▶'}
                </h6>
                {expandedSections.education && (
                  <Row>
                    <Col md={6}>
                      <MultiSelectDropdown
                        label="Institution"
                        field="institutions"
                        options={options.institutions || []}
                        selected={filters.institutions || []}
                      />
                    </Col>
                    <Col md={6}>
                      <MultiSelectDropdown
                        label="Degree"
                        field="degrees"
                        options={options.degrees || []}
                        selected={filters.degrees || []}
                      />
                    </Col>
                    <Col md={6}>
                      <MultiSelectDropdown
                        label="Field of Study"
                        field="fieldOfStudy"
                        options={options.fieldOfStudy || []}
                        selected={filters.fieldOfStudy || []}
                      />
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Graduation Year</Label>
                        <div className="d-flex gap-2">
                          <Input
                            type="number"
                            placeholder="From"
                            value={localFilters.graduationYear?.from || ''}
                            onChange={(e) =>
                              setLocalFilters({
                                ...localFilters,
                                graduationYear: { ...localFilters.graduationYear, from: e.target.value ? Number(e.target.value) : '' },
                              })
                            }
                          />
                          <Input
                            type="number"
                            placeholder="To"
                            value={localFilters.graduationYear?.to || ''}
                            onChange={(e) =>
                              setLocalFilters({
                                ...localFilters,
                                graduationYear: { ...localFilters.graduationYear, to: e.target.value ? Number(e.target.value) : '' },
                              })
                            }
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="mb-4">
                <h6
                  className="mb-3 d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSection('professional')}
                >
                  Professional {expandedSections.professional ? '▼' : '▶'}
                </h6>
                {expandedSections.professional && (
                  <Row>
                    <Col md={6}>
                      <MultiSelectDropdown
                        label="Organization"
                        field="organizations"
                        options={options.organizations || []}
                        selected={filters.organizations || []}
                      />
                    </Col>
                    <Col md={6}>
                      <MultiSelectDropdown
                        label="Designation"
                        field="designations"
                        options={options.designations || []}
                        selected={filters.designations || []}
                      />
                    </Col>
                  </Row>
                )}
              </div>
            </Col>
          </Row>
          {/* Gender and Status */}
          <Row >
            <Col md={6}>
              <MultiSelectDropdown
                label="Gender"
                field="gender"
                options={uniqueValues.genders}
              />
            </Col>
            <Col md={6}>
              <MultiSelectDropdown
                label="Status"
                field="status"
                options={uniqueValues.statuses}
              />
            </Col>
          </Row>

          {/* Review Status */}
          <Row >
            <Col md={12}>
              <MultiSelectDropdown
                label="Review Status"
                field="reviewStatus"
                options={uniqueValues.reviewStatuses || []}
              />
            </Col>
          </Row>

          {/* State and Country */}
          <Row >
            <Col md={6}>
              <MultiSelectDropdown
                label="State"
                field="state"
                options={uniqueValues.states}
              />
            </Col>
            <Col md={6}>
              <MultiSelectDropdown
                label="Country"
                field="country"
                options={uniqueValues.countries}
              />
            </Col>
          </Row>

          {/* Reviewer Assignment */}
          <Row >
            <Col md={12}>
              <FormGroup>
                <Label>Reviewer Assignment</Label>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={localFilters.unassigned}
                    onChange={(e) =>
                      setLocalFilters({ ...localFilters, unassigned: e.target.checked })
                    }
                  />
                  <span className="small">Unassigned</span>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleReset}>
          Reset All
        </Button>
        <Button color="primary" onClick={handleApply}>
          Apply Filters
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FilterPanel;
